import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { IcPlus, IcClose, IcSave, IcInfo, IcTrash, IcPackageOpen, IcImage, IcTags, IcSearch, IcChevronRight } from '../../theme/icons';
import productService from '../../services/productService';
import categoryService from '../../services/categoryService';
import subcategoryService from '../../services/subcategoryService';
import laboratoireService from '../../services/laboratoireService';
import {
  normProductName, duplicateProductAlert, extractApiErrorMessage, isDuplicateProductNameError,
} from '../../utils/duplicateProduct';
import { useCompany } from '../../context/CompanyContext';
import { BtnSk } from '../../components/Skeleton';
import { filterSearch } from '../../utils/search';

// Pricing helpers for the product form. The TVA and default margin rates come
// from the company settings (Paramètres > Tarification) at runtime; see the
// component body where TVA_RATE / DEFAULT_MARGIN_RATE are derived.
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const round3 = (n) => Math.round((n + Number.EPSILON) * 1000) / 1000;

// Searchable picker styled like the « Informations Fournisseur » block of the
// facture page: a search input with a dropdown of matches, the « Ajouter … »
// action at the BOTTOM of the dropdown (no + button on the right), and the
// selection shown as a blue card with an ✕ to change it.
// `openUp` : la liste s'ouvre AU-DESSUS du champ (Laboratoire, en bas de la
// carte : vers le bas elle serait rognée par la carte / la zone qui défile).
const SearchSelect = ({ inputId, items, valueId, onPick, placeholder, emptyLabel, addLabel, onAdd, disabled, openUp = false }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const selected = items.find((i) => String(i.id) === String(valueId));
  // Règle Ki Para (utils/search.js) : début de mot, sans accents.
  const filtered = filterSearch(items, query, { text: (i) => i.name });

  if (selected) {
    return (
      <div className="relative bg-blue-50 border border-blue-100 rounded-lg px-3 py-2.5 flex items-center justify-between text-sm">
        <span className="font-medium text-blue-900 truncate">{selected.name}</span>
        <button
          type="button"
          onClick={() => onPick(null)}
          className="text-gray-400 hover:text-red-600 ml-3 shrink-0 transition-colors"
          title="Changer"
        >
          <IcClose />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <IcSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" size={13} />
      <input
        id={inputId}
        type="text"
        value={query}
        disabled={disabled}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onClick={() => !disabled && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="inp w-full pl-9 pr-3 py-2.5 text-sm text-gray-900 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        placeholder={placeholder}
        autoComplete="off"
      />
      {open && !disabled && (
        <div className={`absolute left-0 right-0 z-30 bg-white rounded-lg shadow-lg border border-gray-100 py-1 max-h-52 overflow-y-auto ${openUp ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
          {filtered.length > 0 ? (
            filtered.map((item) => (
              <div
                key={item.id}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onPick(item); setQuery(''); setOpen(false); }}
                className="px-3 py-1.5 hover:bg-gray-50 cursor-pointer"
              >
                <div className="text-sm font-medium text-gray-800 truncate">{item.name}</div>
              </div>
            ))
          ) : (
            <div className="px-3 py-1.5 text-gray-500 text-xs">{emptyLabel}</div>
          )}
          <div
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { setOpen(false); onAdd(query.trim()); setQuery(''); }}
            className="btn-add-link w-full px-3 py-1.5 text-xs cursor-pointer border-t border-gray-100 mt-1"
          >
            <IcPlus size={9} />
            {addLabel}
          </div>
        </div>
      )}
    </div>
  );
};

// Shared input styling (matches the modern look used across the app)
// Formulaire (Brand Kit §05) : champ .inp 42px bordé #D1D5DB rayon 6,
// 13.5px, padding 11, focus 2px encre ; libellé 12px 500 encre, mb 4.
// Mêmes recettes que le panneau de « Nouvelle vente » (owner 2026-09-09) :
// champ `.inp` 13.5px py-2.5 rayon 8, libellé 12px 500 encre mb-1.5, aide
// 11px gris, suffixe DT / % gris posé à droite du champ.
const inputClass = 'inp w-full px-3 py-2.5 text-sm text-gray-900';
const textareaClass = 'inp w-full px-3 py-2.5 text-sm text-gray-900';
const labelClass = 'block text-xs font-medium text-gray-900 mb-1.5';
const helpClass = 'text-[11px] text-gray-400 mt-1.5';
const suffixClass = 'absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none';

const AddProduct = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const location = useLocation();

  // Pricing rates from company settings (Paramètres > Tarification), as ratios.
  // Fallbacks (19% / 50%) match the previous hardcoded values.
  const { marge: companyMarge, tva: companyTva } = useCompany();
  const TVA_RATE = (Number(companyTva) || 19) / 100;
  const DEFAULT_MARGIN_RATE = (Number(companyMarge) || 50) / 100;

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [filteredSubcategories, setFilteredSubcategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    cost: '',
    price: '',
    remise: 0,
    stock: 0,
    stock_min: 0,
    category: '',
    subcategory: '',
    laboratoire: '',
    description: ''
  });
  // Profit margin % shown between Prix d'achat and Prix de vente (derived, not persisted).
  const [marginPercent, setMarginPercent] = useState('');

  // Préremplissage depuis un bon de commande (réception d'un « nouveau
  // produit » réservé) : nom + prix d'achat attendus.
  useEffect(() => {
    const prefill = !isEditMode ? location.state?.prefill : null;
    if (!prefill) return;
    setFormData((prev) => ({
      ...prev,
      name: prefill.name || prev.name,
      cost: prefill.cost != null && prefill.cost !== '' ? String(prefill.cost) : prev.cost,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Product photo upload (File + local preview).
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  // Panneau « Photo du produit » (owner 2026-09-09) : ouvert par « Voir » dans
  // le champ, montre la photo choisie + Changer / Supprimer.
  const [showPhotoPanel, setShowPhotoPanel] = useState(false);
  const imageInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(isEditMode);
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [subcategoryLoading, setSubcategoryLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Modal states
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [newCategoryData, setNewCategoryData] = useState({ name: '', description: '', status: 'ACTIVE' });
  const [newSubcategoryData, setNewSubcategoryData] = useState({ name: '', description: '', category_id: '', status: 'ACTIVE' });
  const [laboratoires, setLaboratoires] = useState([]);
  const [laboratoireLoading, setLaboratoireLoading] = useState(true);
  const [showLaboratoireModal, setShowLaboratoireModal] = useState(false);
  const [newLaboratoireData, setNewLaboratoireData] = useState({ name: '', description: '' });

  // Valeurs telles que chargées à l'ouverture de la fiche. Sert à n'envoyer que
  // les champs réellement modifiés : le stock bouge tout seul (ventes,
  // factures) pendant que la fiche est ouverte, et le renvoyer tel quel
  // effacerait ces mouvements.
  const loadedRef = useRef(null);

  // Fetch categories and product data (if edit mode) when component mounts
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoryLoading(true);
        const response = await categoryService.getAllCategories();
        console.log('Categories response in AddProduct:', response);
        // Handle both response structures: nested results or direct array
        const categoryData = response.results || response || [];
        console.log('Categories data:', categoryData);
        setCategories(categoryData);
        setCategoryLoading(false);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setCategoryLoading(false);
      }
    };
    
    const fetchProductData = async () => {
      if (!id) return;
      
      try {
        setLoadingProduct(true);
        console.log('Fetching product with ID:', id);
        const productData = await productService.getProductById(id);
        console.log('Product data received:', productData);
        
        const loaded = {
          name: productData.name || '',
          code: productData.code || '',
          cost: productData.cost ? productData.cost.toString() : '',
          price: productData.price ? productData.price.toString() : '',
          remise: productData.remise !== undefined && productData.remise !== null ? productData.remise : 0,
          stock: productData.stock !== undefined ? productData.stock : 0,
          stock_min: productData.stock_min !== undefined ? productData.stock_min : 0,
          category: productData.category && productData.category.id ? productData.category.id.toString() : '',
          subcategory: productData.subcategory && productData.subcategory.id ? productData.subcategory.id.toString() : '',
          laboratoire: productData.laboratoire && productData.laboratoire.id ? productData.laboratoire.id.toString() : '',
          description: productData.description || ''
        };
        loadedRef.current = loaded;
        setFormData(loaded);
        // Derive the profit margin % from the saved cost + price.
        const costNum = parseFloat(productData.cost);
        const priceNum = parseFloat(productData.price);
        if (!isNaN(costNum) && costNum > 0 && !isNaN(priceNum)) {
          const base = costNum * (1 + TVA_RATE);
          setMarginPercent(String(round2(((priceNum - base) / base) * 100)));
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        setError(`Unable to load product data (ID: ${id}). ${err.message || 'Please try again.'}`);
      } finally {
        setLoadingProduct(false);
      }
    };

    fetchCategories();
    if (isEditMode) {
      fetchProductData();
    }
  }, [id, isEditMode]);
  
  // Fetch all subcategories
  useEffect(() => {
    const fetchSubcategories = async () => {
      try {
        setSubcategoryLoading(true);
        const response = await subcategoryService.getAllSubcategories();
        console.log('Subcategories response:', response);
        const subcategoryData = response.results || response || [];
        setSubcategories(subcategoryData);
        setSubcategoryLoading(false);
      } catch (err) {
        console.error('Error fetching subcategories:', err);
        setSubcategoryLoading(false);
      }
    };
    
    fetchSubcategories();
  }, []);

  // Fetch all laboratoires
  useEffect(() => {
    const fetchLaboratoires = async () => {
      try {
        setLaboratoireLoading(true);
        const response = await laboratoireService.getAllLaboratoires();
        const data = response.results || response || [];
        setLaboratoires(data);
      } catch (err) {
        console.error('Error fetching laboratoires:', err);
      } finally {
        setLaboratoireLoading(false);
      }
    };
    fetchLaboratoires();
  }, []);

  // Filter subcategories when category changes
  useEffect(() => {
    if (formData.category) {
      const filtered = subcategories.filter(sub => sub.category == formData.category || sub.category_id == formData.category);
      setFilteredSubcategories(filtered);
    } else {
      setFilteredSubcategories([]);
      // Clear subcategory if category is cleared
      if (formData.subcategory) {
        setFormData(prev => ({ ...prev, subcategory: '' }));
      }
    }
  }, [formData.category, subcategories]);
  
  // Filter categories based on search term
  const [categorySearch, setCategorySearch] = useState('');
  const filteredCategories = filterSearch(categories, categorySearch, { text: (c) => c.name });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  // Prix d'achat HT changed. NEW product: autofill prix de vente (HT + TVA + default
  // margin) and set marge 50%. EXISTING product: keep prix de vente, just recompute
  // the marge from it — so adding a cost to an old product doesn't change its sale price.
  const handleCostChange = (e) => {
    const raw = e.target.value;
    const cost = parseFloat(raw);
    if (isNaN(cost) || cost <= 0) {
      // No valid cost: clear marge; keep the existing price in edit mode, clear it when adding.
      setFormData(prev => (isEditMode ? { ...prev, cost: raw } : { ...prev, cost: raw, price: '' }));
      setMarginPercent('');
      return;
    }
    const base = cost * (1 + TVA_RATE);
    if (isEditMode) {
      // Existing product: keep prix de vente, derive marge from it.
      setFormData(prev => ({ ...prev, cost: raw }));
      const price = parseFloat(formData.price);
      setMarginPercent(!isNaN(price) ? String(round2(((price - base) / base) * 100)) : '');
    } else {
      // New product: autofill prix de vente + default margin.
      setFormData(prev => ({ ...prev, cost: raw, price: String(round3(base * (1 + DEFAULT_MARGIN_RATE))) }));
      setMarginPercent(String(round2(DEFAULT_MARGIN_RATE * 100)));
    }
  };

  // Prix de vente edited -> recompute the profit % from it.
  const handlePriceChange = (e) => {
    const raw = e.target.value;
    setFormData(prev => ({ ...prev, price: raw }));
    const price = parseFloat(raw);
    const cost = parseFloat(formData.cost);
    if (!isNaN(price) && !isNaN(cost) && cost > 0) {
      const base = cost * (1 + TVA_RATE);
      setMarginPercent(String(round2(((price - base) / base) * 100)));
    } else {
      setMarginPercent('');
    }
  };

  // Profit % edited -> recompute prix de vente.
  const handlePercentChange = (e) => {
    const raw = e.target.value;
    setMarginPercent(raw);
    const pct = parseFloat(raw);
    const cost = parseFloat(formData.cost);
    if (!isNaN(pct) && !isNaN(cost) && cost > 0) {
      setFormData(prev => ({ ...prev, price: String(round3(cost * (1 + TVA_RATE) * (1 + pct / 100))) }));
    }
  };

  // Photo selected -> keep the File for submit + a local preview (not surfaced anywhere yet).
  const handleImageChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setImageFile(file || null);
    setImagePreview(file ? URL.createObjectURL(file) : null);
  };
  // Supprimer (panneau) : on oublie la photo choisie et on vide l'<input
  // file> pour qu'un nouveau choix du même fichier redéclenche onChange.
  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (imageInputRef.current) imageInputRef.current.value = '';
    setShowPhotoPanel(false);
  };

  // Édition : diff entre ce qui est affiché et ce qui avait été chargé. Seuls
  // les champs réellement touchés partent au serveur — enregistrer une fiche
  // pour corriger un prix ne doit plus réécrire le stock.
  const changedProductFields = () => {
    const base = loadedRef.current || {};
    const same = (a, b) => String(a ?? '') === String(b ?? '');
    const out = {};
    if (!same(formData.name, base.name)) out.name = formData.name;
    if (!same(formData.code, base.code)) out.code = formData.code || '';
    if (!same(formData.description, base.description)) out.description = formData.description || '';
    if (!same(formData.cost, base.cost)) out.cost = parseFloat(formData.cost) || 0;
    if (!same(formData.price, base.price)) out.price = parseFloat(formData.price);
    if (!same(formData.remise, base.remise)) out.remise = parseFloat(formData.remise) || 0;
    if (!same(formData.stock_min, base.stock_min)) out.stock_min = parseInt(formData.stock_min, 10) || 0;
    if (!same(formData.category, base.category)) out.category = formData.category || null;
    if (!same(formData.subcategory, base.subcategory)) out.subcategory = formData.subcategory || null;
    if (!same(formData.laboratoire, base.laboratoire)) out.laboratoire = formData.laboratoire || null;
    // Le stock ne part que si l'utilisateur l'a lui-même changé, toujours
    // accompagné de la valeur lue à l'ouverture : le serveur refuse alors
    // l'écriture si une vente ou une facture est passée entre-temps.
    if (!same(formData.stock, base.stock)) {
      out.stock = parseInt(formData.stock, 10) || 0;
      out.expected_stock = parseInt(base.stock, 10) || 0;
    }
    return out;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Le nom du produit est requis.');
      return;
    }

    if (isNaN(parseFloat(formData.price)) || parseFloat(formData.price) <= 0) {
      setError('Veuillez entrer un prix valide.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const createPayload = {
        name: formData.name,
        code: formData.code || '',
        description: formData.description || '',
        cost: parseFloat(formData.cost) || 0,
        price: parseFloat(formData.price),
        remise: parseFloat(formData.remise) || 0,
        stock: parseInt(formData.stock, 10) || 0,
        stock_min: parseInt(formData.stock_min, 10) || 0,
        category: formData.category || null,
        subcategory: formData.subcategory || null,
        laboratoire: formData.laboratoire || null,
      };
      const payload = isEditMode ? changedProductFields() : createPayload;

      if (imageFile) {
        // Multipart submit (a photo was selected). Omit empty FKs — multipart can't carry null.
        const fd = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (value !== null && value !== undefined) fd.append(key, value);
        });
        fd.append('image', imageFile);
        if (isEditMode) {
          await productService.patchProductForm(id, fd);
        } else {
          await productService.createProductForm(fd);
        }
      } else if (isEditMode) {
        // Aucun champ touché → rien à envoyer, on referme simplement la fiche.
        if (Object.keys(payload).length > 0) {
          await productService.patchProduct(id, payload);
        }
      } else {
        await productService.createProduct(payload);
      }

      navigate('/products');
    } catch (err) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} product:`, err);
      // Le stock a bougé pendant que la fiche était ouverte : le serveur a
      // refusé l'écriture plutôt que d'effacer les mouvements. On explique quoi
      // faire au lieu d'afficher une erreur technique.
      if (err && err.error === 'stock_conflict') {
        setLoading(false);
        setError(err.detail);
        return;
      }
      const message = extractApiErrorMessage(err);
      setLoading(false);
      // Nom en doublon → carte « Produit déjà au catalogue » (brand kit) avec
      // la fiche existante, plutôt qu'un bandeau d'erreur générique.
      if (isDuplicateProductNameError(message)) {
        let existing = null;
        try {
          const all = await productService.getAllProducts();
          const list = all.results || all || [];
          existing = list.find((p) => normProductName(p.name) === normProductName(formData.name)) || null;
        } catch (fetchErr) {
          console.error('Recherche de la fiche existante:', fetchErr);
        }
        const r = await duplicateProductAlert({
          product: existing, name: formData.name,
          confirmText: existing ? 'Ouvrir la fiche existante' : undefined,
        });
        if (r.isConfirmed && existing) navigate(`/products/${existing.id}/edit`);
        return;
      }
      setError(message || `Une erreur est survenue lors de ${isEditMode ? 'la modification' : 'la création'} du produit.`);
    }
  };
  
  // Handlers for new category modal (opened from the bottom of the search
  // dropdown — the typed query pre-fills the name)
  const handleCloseCategoryModal = () => {
    setShowCategoryModal(false);
    setNewCategoryData({ name: '', description: '', status: 'ACTIVE' });
  };
  
  const handleSaveNewCategory = async (e) => {
    e.preventDefault();
    try {
      const createdCategory = await categoryService.createCategory(newCategoryData);
      setCategories([...categories, createdCategory]);
      setFormData({ ...formData, category: createdCategory.id.toString() });
      handleCloseCategoryModal();
    } catch (err) {
      console.error('Error creating category:', err);
      setError('Erreur lors de la création de la catégorie');
    }
  };
  
  // Handlers for new subcategory modal
  const handleCloseSubcategoryModal = () => {
    setShowSubcategoryModal(false);
    setNewSubcategoryData({ name: '', description: '', category_id: '', status: 'ACTIVE' });
  };
  
  const handleSaveNewSubcategory = async (e) => {
    e.preventDefault();
    try {
      const createdSubcategory = await subcategoryService.createSubcategory(newSubcategoryData);
      setSubcategories([...subcategories, createdSubcategory]);
      setFormData({ ...formData, subcategory: createdSubcategory.id.toString() });
      handleCloseSubcategoryModal();
    } catch (err) {
      console.error('Error creating subcategory:', err);
      setError('Erreur lors de la création de la sous-catégorie');
    }
  };

  // Handlers for new laboratoire modal
  const handleCloseLaboratoireModal = () => {
    setShowLaboratoireModal(false);
    setNewLaboratoireData({ name: '', description: '' });
  };

  const handleSaveNewLaboratoire = async (e) => {
    e.preventDefault();
    if (!newLaboratoireData.name.trim()) return;
    try {
      const created = await laboratoireService.createLaboratoire(newLaboratoireData);
      // The backend returns the EXISTING laboratoire when the name already
      // exists (dedupe) — don't append a second copy to the local list.
      setLaboratoires(laboratoires.some((l) => l.id === created.id) ? laboratoires : [...laboratoires, created]);
      setFormData({ ...formData, laboratoire: created.id.toString() });
      handleCloseLaboratoireModal();
    } catch (err) {
      console.error('Error creating laboratoire:', err);
      setError('Erreur lors de la création du laboratoire');
    }
  };

  // Delete lives here (in the edit screen) instead of the detail popup.
  const handleDelete = async () => {
    if (!isEditMode) return;
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) return;
    setDeleting(true);
    try {
      await productService.deleteProduct(id);
      navigate('/products');
    } catch (err) {
      console.error('Error deleting product:', err);
      setError(err.message || 'Une erreur est survenue lors de la suppression du produit.');
      setDeleting(false);
    }
  };

  // Même cadre que « Nouvelle vente » (owner 2026-09-09) : la page EST le bloc
  // `w-full px-4 pt-2 pb-4`, chip de page (même `mb-4` que VenteTabs, plus
  // AUCUN écart supplémentaire), puis la scission `.kp-split` du kit
  // (index.css) : 1fr / 360px, gouttière 16px, deux `.kp-pane` collantes de
  // hauteur fixe `--kp-work-h` dont seul l'intérieur défile (`.kp-scroll`,
  // barre masquée) — exactement les deux cartes de /seller-sales, à la MÊME
  // hauteur `--kp-work-h` ; la barre d'outils absente est compensée par une
  // marge haute mesurée sur la scission (owner 2026-09-09), donc les cartes
  // commencent et finissent au pixel près comme sur /seller-sales.
  // Gauche = carte « Informations générales » (Nom+Code / Catégorie+Sous-cat /
  // Description / Photo+Laboratoire sur la même ligne / Stock+Alerte). Droite = carte « Prix »
  // `.kp-card` (en-tête + zone qui défile + pied ; le champ « Remise (offre) »
  // retiré du panneau le 2026-09-09 — l'état `remise` reste envoyé tel quel)
  // dont le pied porte Annuler
  // (ghost) + Enregistrer (encre, primaire, 2/3 de la ligne).
  // Aucune logique touchée : mêmes handlers, mêmes champs, même envoi.
  // Bouton Enregistrer INACTIF tant que les champs obligatoires ne sont pas
  // remplis (owner 2026-09-09) — même règle que « Enregistrer la facture »
  // (/facturation/add) et /clients/add : .btn-p grisé via :disabled.
  // Obligatoires : nom et prix de vente > 0 (mêmes règles que handleSubmit).
  const canSave = Boolean(formData.name && formData.name.trim()) && parseFloat(formData.price) > 0;

  return (
    <div className="w-full px-4 pt-2 pb-4">
      {/* Nom de page = le chip ACTIF de « Nouvelle vente » (owner 2026-09-09) :
          recette `.tab-chip-on` (blanc, bordure encre 2 px, rayon 16, 42 px,
          13.5px 600, icône 13). Enveloppe `flex` → rangée de 42 px exactement. */}
      <div className="mb-4 flex">
        <h1 className="tab-chip-on cursor-default">
          <IcPackageOpen size={13} /> {isEditMode ? 'Modifier le produit' : 'Ajouter un nouveau produit'}
        </h1>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Même marge haute que sur /seller-sales, mesurée : là-bas les cartes
            commencent 80 px sous le chip de 42 px (rangée d'onglets 46 px −
            42 = 4, + mb-4 16, + barre recherche/Filtres 48, + mb-3 12). La
            marge fusionne avec le mb-4 du chip, donc mt-20 = 80 px net
            (owner 2026-09-09). */}
        <div className="kp-split mt-20">

          {/* ================= Colonne gauche : Informations générales ================= */}
          <div className="kp-pane bg-white border border-gray-200 rounded-2xl overflow-hidden">
            {/* En-tête de carte — même filet que le panneau de « Nouvelle vente » */}
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 flex-shrink-0">
              <span className="h-7 w-7 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center shrink-0"><IcPackageOpen size={14} /></span>
              <h2 className="text-sm font-bold text-gray-900">Informations générales</h2>
            </div>

            <div className="kp-scroll p-4 space-y-4">
              {/* Ligne 1 — Nom | Code */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="min-w-0">
                  <label className={labelClass} htmlFor="name">Nom <span className="req">*</span></label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Nom du produit"
                    required
                  />
                </div>
                <div className="min-w-0">
                  <label className={labelClass} htmlFor="code">Code</label>
                  <input
                    type="text"
                    name="code"
                    id="code"
                    value={formData.code}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Code du produit (optionnel)"
                  />
                </div>
              </div>

              {/* Ligne 2 — Catégorie | Sous-catégorie */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="min-w-0">
                  <label className={labelClass} htmlFor="category">Catégorie</label>
                  <SearchSelect
                    inputId="category"
                    items={categories}
                    valueId={formData.category}
                    onPick={(item) => setFormData((prev) => ({ ...prev, category: item ? item.id.toString() : '', subcategory: '' }))}
                    placeholder="Rechercher une catégorie..."
                    emptyLabel="Aucune catégorie trouvée"
                    addLabel="Ajouter une nouvelle catégorie"
                    onAdd={(q) => { setNewCategoryData({ name: q, description: '', status: 'ACTIVE' }); setShowCategoryModal(true); }}
                    disabled={categoryLoading}
                  />
                </div>
                <div className="min-w-0">
                  <label className={labelClass} htmlFor="subcategory">Sous-catégorie</label>
                  <SearchSelect
                    inputId="subcategory"
                    items={filteredSubcategories}
                    valueId={formData.subcategory}
                    onPick={(item) => setFormData((prev) => ({ ...prev, subcategory: item ? item.id.toString() : '' }))}
                    placeholder="Rechercher une sous-catégorie..."
                    emptyLabel="Aucune sous-catégorie trouvée"
                    addLabel="Ajouter une nouvelle sous-catégorie"
                    onAdd={(q) => { setNewSubcategoryData({ name: q, description: '', category_id: formData.category || '', status: 'ACTIVE' }); setShowSubcategoryModal(true); }}
                    disabled={!formData.category || subcategoryLoading}
                  />
                  {!formData.category && (
                    <p className={helpClass}>Veuillez d'abord sélectionner une catégorie</p>
                  )}
                </div>
              </div>

              {/* Ligne 3 — Description (pleine largeur) */}
              <div>
                <label className={labelClass} htmlFor="description">Description</label>
                <textarea
                  name="description"
                  id="description"
                  value={formData.description}
                  onChange={handleChange}
                  className={`${textareaClass} min-h-[96px] resize-y`}
                  placeholder="Description du produit"
                  rows="4"
                />
              </div>

              {/* Ligne 4 — Photo du produit | Laboratoire sur la même ligne
                  (owner 2026-09-09). Le champ photo est un champ `.inp` comme
                  ses voisins (42 px, icône à gauche, texte 13.5px) avec
                  l'action « Choisir » / « Changer » en `.btn-add-link` à
                  droite — même vocabulaire que ServiceForm / Paramètres
                  (« Choisir une image » / « Changer l'image »). L'<input
                  type="file"> reste le seul mécanisme (id/name/accept/onChange
                  inchangés). */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="min-w-0">
                  <label className={labelClass} htmlFor="image">Photo du produit</label>
                  <label
                    htmlFor="image"
                    className="inp w-full pl-3 pr-3 py-2.5 text-sm flex items-center gap-2.5 cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    {imagePreview ? (
                      <img src={imagePreview} alt="Aperçu" className="h-5 w-5 object-cover rounded border border-gray-200 shrink-0" />
                    ) : (
                      <IcImage size={13} className="text-gray-400 shrink-0" />
                    )}
                    <span className={`min-w-0 flex-1 truncate ${imageFile ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
                      {imageFile ? imageFile.name : 'Ajouter une photo'}
                    </span>
                    {imageFile ? (
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowPhotoPanel(true); }}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-semibold text-iface-600 hover:text-gray-900 shrink-0"
                      >
                        Voir <IcChevronRight className="text-xs" />
                      </button>
                    ) : (
                      <span className="inline-flex items-center justify-center h-6 w-6 rounded-md border-[1.5px] border-gray-200 text-sm font-normal text-gray-400 shrink-0" aria-label="Choisir une photo"><IcPlus size={12} /></span>
                    )}
                  </label>
                  <input
                    ref={imageInputRef}
                    type="file"
                    id="image"
                    name="image"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="sr-only"
                  />
                </div>

                <div className="min-w-0">
                  <label className={labelClass} htmlFor="laboratoire">Laboratoire</label>
                  <SearchSelect
                    openUp
                    inputId="laboratoire"
                    items={laboratoires}
                    valueId={formData.laboratoire}
                    onPick={(item) => setFormData((prev) => ({ ...prev, laboratoire: item ? item.id.toString() : '' }))}
                    placeholder="Rechercher un laboratoire..."
                    emptyLabel="Aucun laboratoire trouvé"
                    addLabel="Ajouter un nouveau laboratoire"
                    onAdd={(q) => { setNewLaboratoireData({ name: q, description: '' }); setShowLaboratoireModal(true); }}
                    disabled={laboratoireLoading}
                  />
                </div>
              </div>

              {/* Ligne 5 — Stock | Niveau d'alerte de stock */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="min-w-0">
                  <label className={labelClass} htmlFor="stock">Stock</label>
                  <input
                    type="number"
                    name="stock"
                    id="stock"
                    value={formData.stock}
                    onChange={handleChange}
                    className={`${inputClass} tabular-nums`}
                    placeholder="Quantité"
                    min="0"
                  />
                </div>
                <div className="min-w-0">
                  <label className={labelClass} htmlFor="stock_min">Niveau d'alerte de stock</label>
                  <input
                    type="number"
                    name="stock_min"
                    id="stock_min"
                    value={formData.stock_min}
                    onChange={handleChange}
                    className={`${inputClass} tabular-nums`}
                    placeholder="Stock minimum pour alerte"
                    min="0"
                  />
                  <p className={helpClass}>Un message d'alerte s'affiche lorsque le stock est inférieur à ce niveau</p>
                </div>
              </div>
            </div>
          </div>

          {/* ================= Colonne droite : Prix (collante) ================= */}
          <div className="kp-card kp-pane bg-white border border-gray-200 rounded-2xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 flex-shrink-0">
              <span className="h-7 w-7 rounded-md bg-gray-100 text-gray-500 flex items-center justify-center shrink-0"><IcTags size={14} /></span>
              <h2 className="text-sm font-bold text-gray-900">Prix <span className="req">*</span></h2>
              {/* Info-bulle : formule du prix de vente. Owner 2026-09-10 : elle s'ouvrait
                  vers le HAUT (bottom-full) depuis l'en-tête d'une carte `overflow-hidden`
                  → rognée, invisible au survol. Ouverte vers le bas (top-full) + z-50. */}
              <span className="relative group inline-flex items-center ml-auto">
                <IcInfo size={14} className="text-gray-400 hover:text-gray-600 cursor-help" tabIndex={0} />
                <span className="pointer-events-none absolute right-0 top-full mt-2 hidden group-hover:block group-focus-within:block z-50 w-72 rounded-md bg-gray-800 text-white text-xs font-normal normal-case p-3 shadow-lg leading-relaxed">
                  <span className="block font-semibold mb-1">Calcul du prix de vente</span>
                  Prix de vente = (Prix d'achat HT + TVA {round2(TVA_RATE * 100)}%) × (1 + marge)
                  <span className="block mt-1">Marge par défaut : {round2(DEFAULT_MARGIN_RATE * 100)}%</span>
                  <span className="block">Modifiable dans Paramètres → Tarification</span>
                </span>
              </span>
            </div>

            <div className="kp-scroll p-4 space-y-4">
              {/* Prix d'achat HT */}
              <div>
                <label className={labelClass} htmlFor="cost">Prix d'achat HT</label>
                <div className="relative">
                  <input
                    type="number"
                    name="cost"
                    id="cost"
                    value={formData.cost}
                    onChange={handleCostChange}
                    className={`${inputClass} pr-10 tabular-nums`}
                    placeholder="0.00"
                    step="0.001"
                    min="0"
                  />
                  <span className={suffixClass}>DT</span>
                </div>
              </div>

              {/* Marge */}
              <div>
                <label className={labelClass} htmlFor="marginPercent">Marge</label>
                <div className="relative">
                  <input
                    type="number"
                    id="marginPercent"
                    value={marginPercent}
                    onChange={handlePercentChange}
                    className={`${inputClass} pr-8 tabular-nums`}
                    placeholder="0"
                    step="0.01"
                  />
                  <span className={suffixClass}>%</span>
                </div>
              </div>

              {/* Prix de vente — point focal, comme « Montant payé » du panneau de vente */}
              <div>
                <label className={labelClass} htmlFor="price">Prix de vente <span className="req">*</span></label>
                <div className="relative">
                  <input
                    type="number"
                    name="price"
                    id="price"
                    value={formData.price}
                    onChange={handlePriceChange}
                    className={`${inputClass} pr-10 text-base font-bold tabular-nums`}
                    placeholder="0.00"
                    step="0.001"
                    min="0"
                    required
                  />
                  <span className={suffixClass}>DT</span>
                </div>
              </div>
            </div>

            {/* Pied — actions, même recette que le pied du panneau de vente :
                ghost sur 1/3, primaire (encre, icône Enregistrer) sur 2/3. */}
            <div className="p-3 border-t border-gray-100 flex-shrink-0">
              <div className="grid grid-cols-3 gap-2">
                <Link to="/products" className="btn-g col-span-1 py-2.5 rounded-xl text-sm transition-colors">
                  Annuler
                </Link>
                <button
                  type="submit"
                  disabled={loading || !canSave}
                  title={canSave ? undefined : 'Remplissez les champs obligatoires'}
                  className="btn-p col-span-2 py-2.5 rounded-xl text-sm transition-colors"
                >
                  {loading ? (
                    <>
                      <BtnSk />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <IcSave size={14} /> {isEditMode ? 'Mettre à jour' : 'Enregistrer'}
                    </>
                  )}
                </button>
              </div>
              {/* Supprimer (mode édition seulement) — action destructive
                  séparée des actions principales, famille `.btn-db`. */}
              {isEditMode && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting || loading}
                  className="btn-db w-full mt-2 py-2.5 rounded-xl text-sm transition-colors"
                >
                  <IcTrash size={14} /> {deleting ? 'Suppression...' : 'Supprimer'}
                </button>
              )}
            </div>
          </div>
        </div>
      </form>

      {/* Panneau « Photo du produit » (owner 2026-09-09) = la carte d'aperçu
          produit de « Nouvelle vente » (clic sur un produit) : backdrop encre,
          carte blanche max-w-sm rayon 16 ombre, zone photo h-64 blanche à
          filet bas avec le ✕ rond gris posé dessus, puis corps p-4 : nom du
          produit en gras (sans nom de fichier). Les actions Supprimer
          (.btn-db) / Changer (.btn-p) restent, alignées à droite. Plus
          d'entête encre ni de pied à filet. */}
      {showPhotoPanel && imagePreview && (
        <div
          className="modal-backdrop z-[80] flex items-center justify-center p-4"
          onClick={() => setShowPhotoPanel(false)}
          onKeyDown={(e) => { if (e.key === 'Escape') setShowPhotoPanel(false); }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-64 bg-white flex items-center justify-center p-4 border-b border-gray-100">
              <img src={imagePreview} alt={imageFile ? imageFile.name : 'Photo du produit'} className="max-h-full max-w-full object-contain" />
              <button
                type="button"
                onClick={() => setShowPhotoPanel(false)}
                className="absolute top-2 right-2 h-8 w-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center"
                aria-label="Fermer"
              >
                <IcClose size={13} />
              </button>
            </div>
            <div className="p-4">
              <h3 className="font-bold text-gray-900 text-lg leading-tight">{formData.name || 'Photo du produit'}</h3>
              {/* Nom du fichier retiré (owner 2026-09-09). */}
              <div className="flex items-center justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="btn-db px-5 py-2.5 rounded-xl text-sm transition-colors"
                >
                  <IcTrash size={14} /> Supprimer
                </button>
                <button
                  type="button"
                  onClick={() => imageInputRef.current && imageInputRef.current.click()}
                  className="btn-g-edit px-5 py-2.5 rounded-xl text-sm transition-colors"
                >
                  <IcImage size={14} /> Changer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none bg-gray-900/45">
          <div className="relative w-full max-w-md mx-auto my-6">
            <div className="relative flex flex-col w-full bg-white border-0 rounded-2xl shadow-xl outline-none focus:outline-none overflow-hidden">
              {/* Header — Composants-2 §10 : aplat encre + ✕ */}
              <div className="modal-head p-5">
                <h3 className="modal-title text-lg">Ajouter une catégorie</h3>
                <button
                  className="p-1 text-2xl leading-none text-gray-400 hover:text-white"
                  onClick={handleCloseCategoryModal}
                  aria-label="Fermer"
                >
                  <IcClose />
                </button>
              </div>
              
              {/* Body */}
              <div className="relative p-6 flex-auto">
                <form onSubmit={handleSaveNewCategory}>
                  <div className="mb-4">
                    <label className={labelClass} htmlFor="category-name">
                      Nom de la catégorie <span className="req">*</span>
                    </label>
                    <input
                      type="text"
                      id="category-name"
                      value={newCategoryData.name}
                      onChange={(e) => setNewCategoryData({ ...newCategoryData, name: e.target.value })}
                      className={inputClass}
                      placeholder="Nom de la catégorie"
                      required
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label className={labelClass} htmlFor="category-description">
                      Description
                    </label>
                    <textarea
                      id="category-description"
                      value={newCategoryData.description}
                      onChange={(e) => setNewCategoryData({ ...newCategoryData, description: e.target.value })}
                      rows="3"
                      className={textareaClass}
                      placeholder="Description de la catégorie"
                    ></textarea>
                  </div>
                  
                  {/* Footer */}
                  <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100 mt-4">
                    <button
                      type="button"
                      className="btn-g px-5 py-2.5 transition-colors"
                      onClick={handleCloseCategoryModal}
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="btn-p py-2.5 px-5 transition-colors"
                    >
                      <IcSave className="mr-2" />
                      Enregistrer
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Subcategory Modal */}
      {showSubcategoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none bg-gray-900/45">
          <div className="relative w-full max-w-md mx-auto my-6">
            <div className="relative flex flex-col w-full bg-white border-0 rounded-2xl shadow-xl outline-none focus:outline-none overflow-hidden">
              {/* Header — Composants-2 §10 : aplat encre + ✕ */}
              <div className="modal-head p-5">
                <h3 className="modal-title text-lg">Ajouter une sous-catégorie</h3>
                <button
                  className="p-1 text-2xl leading-none text-gray-400 hover:text-white"
                  onClick={handleCloseSubcategoryModal}
                  aria-label="Fermer"
                >
                  <IcClose />
                </button>
              </div>
              
              {/* Body */}
              <div className="relative p-6 flex-auto">
                <form onSubmit={handleSaveNewSubcategory}>
                  <div className="mb-4">
                    <label className={labelClass} htmlFor="subcategory-category">
                      Catégorie parente <span className="req">*</span>
                    </label>
                    <select
                      id="subcategory-category"
                      value={newSubcategoryData.category_id}
                      onChange={(e) => setNewSubcategoryData({ ...newSubcategoryData, category_id: e.target.value })}
                      className={inputClass}
                      required
                    >
                      <option value="">Sélectionner une catégorie</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="mb-4">
                    <label className={labelClass} htmlFor="subcategory-name">
                      Nom de la sous-catégorie <span className="req">*</span>
                    </label>
                    <input
                      type="text"
                      id="subcategory-name"
                      value={newSubcategoryData.name}
                      onChange={(e) => setNewSubcategoryData({ ...newSubcategoryData, name: e.target.value })}
                      className={inputClass}
                      placeholder="Nom de la sous-catégorie"
                      required
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label className={labelClass} htmlFor="subcategory-description">
                      Description
                    </label>
                    <textarea
                      id="subcategory-description"
                      value={newSubcategoryData.description}
                      onChange={(e) => setNewSubcategoryData({ ...newSubcategoryData, description: e.target.value })}
                      rows="3"
                      className={textareaClass}
                      placeholder="Description de la sous-catégorie"
                    ></textarea>
                  </div>
                  
                  {/* Footer */}
                  <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100 mt-4">
                    <button
                      type="button"
                      className="btn-g px-5 py-2.5 transition-colors"
                      onClick={handleCloseSubcategoryModal}
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="btn-p py-2.5 px-5 transition-colors"
                    >
                      <IcSave className="mr-2" />
                      Enregistrer
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Laboratoire Modal */}
      {showLaboratoireModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none bg-gray-900/45">
          <div className="relative w-full max-w-md mx-auto my-6">
            <div className="relative flex flex-col w-full bg-white border-0 rounded-2xl shadow-xl outline-none focus:outline-none overflow-hidden">
              <div className="modal-head p-5">
                <h3 className="modal-title text-lg">Ajouter un laboratoire</h3>
                <button
                  className="p-1 text-2xl leading-none text-gray-400 hover:text-white"
                  onClick={handleCloseLaboratoireModal}
                  aria-label="Fermer"
                >
                  <IcClose />
                </button>
              </div>
              <div className="relative p-6 flex-auto">
                <form onSubmit={handleSaveNewLaboratoire}>
                  <div className="mb-4">
                    <label className={labelClass} htmlFor="laboratoire-name">
                      Nom du laboratoire <span className="req">*</span>
                    </label>
                    <input
                      type="text"
                      id="laboratoire-name"
                      value={newLaboratoireData.name}
                      onChange={(e) => setNewLaboratoireData({ ...newLaboratoireData, name: e.target.value })}
                      className={inputClass}
                      placeholder="Nom du laboratoire"
                      required
                    />
                  </div>
                  <div className="mb-4">
                    <label className={labelClass} htmlFor="laboratoire-description">
                      Description
                    </label>
                    <textarea
                      id="laboratoire-description"
                      value={newLaboratoireData.description}
                      onChange={(e) => setNewLaboratoireData({ ...newLaboratoireData, description: e.target.value })}
                      rows="3"
                      className={textareaClass}
                      placeholder="Description du laboratoire"
                    ></textarea>
                  </div>
                  <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100 mt-4">
                    <button
                      type="button"
                      className="btn-g px-5 py-2.5 transition-colors"
                      onClick={handleCloseLaboratoireModal}
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="btn-p py-2.5 px-5 transition-colors"
                    >
                      <IcSave className="mr-2" />
                      Enregistrer
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddProduct;
